// src/components/common/charts/ChartComponents.js
import React from 'react';
import {
    LineChart, Line, BarChart, Bar, PieChart, Pie, AreaChart, Area,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import { useTheme } from '../../../contexts/ThemeContext';
import { formatChartXAxis } from '../../../utils/dateHelpers';

// Color palette for charts
const COLORS = {
    primary: '#4F46E5',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    info: '#3B82F6',
    purple: '#8B5CF6',
    pink: '#EC4899',
    teal: '#14B8A6',
    orange: '#F97316',
    gray: '#6B7280'
};

const CHART_COLORS = [
    COLORS.primary,
    COLORS.success,
    COLORS.warning,
    COLORS.danger,
    COLORS.info,
    COLORS.purple,
    COLORS.pink,
    COLORS.teal,
    COLORS.orange,
    COLORS.gray
];

/**
 * Line Chart Component
 */
export const LineChartComponent = ({ data, xKey, lines, height = 300, showGrid = true, formatXAxis = false }) => {
    const { isDarkMode } = useTheme();

    // Determine if xKey is a date field based on key name or formatXAxis prop
    const isDateField = formatXAxis || xKey === 'date' || xKey === 'month' || xKey === 'year' || xKey.toLowerCase().includes('date');

    return (
        <ResponsiveContainer width="100%" height={height}>
            <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                {showGrid && <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#E5E7EB'} />}
                <XAxis
                    dataKey={xKey}
                    stroke={isDarkMode ? '#9CA3AF' : '#6B7280'}
                    tickFormatter={isDateField ? formatChartXAxis : undefined}
                    angle={isDateField ? -45 : 0}
                    textAnchor={isDateField ? 'end' : 'middle'}
                    height={isDateField ? 60 : 30}
                />
                <YAxis stroke={isDarkMode ? '#9CA3AF' : '#6B7280'} />
                <Tooltip
                    contentStyle={{
                        backgroundColor: isDarkMode ? '#1F2937' : '#FFFFFF',
                        border: `1px solid ${isDarkMode ? '#374151' : '#E5E7EB'}`,
                        color: isDarkMode ? '#F9FAFB' : '#111827'
                    }}
                    labelFormatter={isDateField ? formatChartXAxis : undefined}
                />
                <Legend wrapperStyle={{ color: isDarkMode ? '#F9FAFB' : '#111827' }} />
                {lines.map((line, index) => (
                    <Line
                        key={line.dataKey}
                        type="monotone"
                        dataKey={line.dataKey}
                        name={line.name}
                        stroke={line.color || CHART_COLORS[index % CHART_COLORS.length]}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                    />
                ))}
            </LineChart>
        </ResponsiveContainer>
    );
};

/**
 * Bar Chart Component
 */
export const BarChartComponent = ({ data, xKey, bars, height = 300, stacked = false, horizontal = false, formatXAxis = false }) => {
    const { isDarkMode } = useTheme();
    const ChartComponent = BarChart;

    // Determine if xKey is a date field
    const isDateField = formatXAxis || xKey === 'date' || xKey === 'month' || xKey === 'year' || xKey.toLowerCase().includes('date');

    return (
        <ResponsiveContainer width="100%" height={height}>
            <ChartComponent data={data} margin={{ top: 5, right: 30, left: 20, bottom: isDateField ? 60 : 5 }} layout={horizontal ? 'vertical' : 'horizontal'}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#E5E7EB'} />
                {horizontal ? (
                    <>
                        <XAxis type="number" stroke={isDarkMode ? '#9CA3AF' : '#6B7280'} />
                        <YAxis
                            dataKey={xKey}
                            type="category"
                            stroke={isDarkMode ? '#9CA3AF' : '#6B7280'}
                            tickFormatter={isDateField ? formatChartXAxis : undefined}
                        />
                    </>
                ) : (
                    <>
                        <XAxis
                            dataKey={xKey}
                            stroke={isDarkMode ? '#9CA3AF' : '#6B7280'}
                            tickFormatter={isDateField ? formatChartXAxis : undefined}
                            angle={isDateField ? -45 : 0}
                            textAnchor={isDateField ? 'end' : 'middle'}
                            height={isDateField ? 60 : 30}
                        />
                        <YAxis stroke={isDarkMode ? '#9CA3AF' : '#6B7280'} />
                    </>
                )}
                <Tooltip
                    contentStyle={{
                        backgroundColor: isDarkMode ? '#1F2937' : '#FFFFFF',
                        border: `1px solid ${isDarkMode ? '#374151' : '#E5E7EB'}`,
                        color: isDarkMode ? '#F9FAFB' : '#111827'
                    }}
                    labelFormatter={isDateField ? formatChartXAxis : undefined}
                />
                <Legend wrapperStyle={{ color: isDarkMode ? '#F9FAFB' : '#111827' }} />
                {bars.map((bar, index) => (
                    <Bar
                        key={bar.dataKey}
                        dataKey={bar.dataKey}
                        name={bar.name}
                        fill={bar.color || CHART_COLORS[index % CHART_COLORS.length]}
                        stackId={stacked ? 'stack' : undefined}
                        radius={[4, 4, 0, 0]}
                    />
                ))}
            </ChartComponent>
        </ResponsiveContainer>
    );
};

/**
 * Pie Chart Component
 */
export const PieChartComponent = ({ data, dataKey, nameKey, height = 300, showLabel = false }) => {
    const { isDarkMode } = useTheme();

    const renderLabel = (entry) => {
        const value = entry[dataKey];
        // Only show label if value is greater than 0
        if (!showLabel || value === 0) return null;
        return `${value}`;
    };

    // Custom label for legend to include values
    const renderLegendText = (value, entry) => {
        const dataValue = entry.payload[dataKey];
        return `${value}: ${dataValue}`;
    };

    return (
        <ResponsiveContainer width="100%" height={height}>
            <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                <Pie
                    data={data}
                    dataKey={dataKey}
                    nameKey={nameKey}
                    cx="35%"
                    cy="50%"
                    innerRadius={0}
                    outerRadius={65}
                    label={renderLabel}
                    labelLine={false}
                >
                    {data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                </Pie>
                <Tooltip
                    contentStyle={{
                        backgroundColor: isDarkMode ? '#1F2937' : '#FFFFFF',
                        border: `1px solid ${isDarkMode ? '#374151' : '#E5E7EB'}`,
                        color: isDarkMode ? '#F9FAFB' : '#111827'
                    }}
                />
                <Legend
                    wrapperStyle={{
                        paddingLeft: '20px',
                        color: isDarkMode ? '#F9FAFB' : '#111827'
                    }}
                    verticalAlign="middle"
                    align="right"
                    layout="vertical"
                    iconType="circle"
                    iconSize={12}
                    formatter={renderLegendText}
                />
            </PieChart>
        </ResponsiveContainer>
    );
};

/**
 * Area Chart Component
 */
export const AreaChartComponent = ({ data, xKey, areas, height = 300, stacked = false, formatXAxis = false }) => {
    const { isDarkMode } = useTheme();

    // Determine if xKey is a date field
    const isDateField = formatXAxis || xKey === 'date' || xKey === 'month' || xKey === 'year' || xKey.toLowerCase().includes('date');

    return (
        <ResponsiveContainer width="100%" height={height}>
            <AreaChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: isDateField ? 60 : 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#E5E7EB'} />
                <XAxis
                    dataKey={xKey}
                    stroke={isDarkMode ? '#9CA3AF' : '#6B7280'}
                    tickFormatter={isDateField ? formatChartXAxis : undefined}
                    angle={isDateField ? -45 : 0}
                    textAnchor={isDateField ? 'end' : 'middle'}
                    height={isDateField ? 60 : 30}
                />
                <YAxis stroke={isDarkMode ? '#9CA3AF' : '#6B7280'} />
                <Tooltip
                    contentStyle={{
                        backgroundColor: isDarkMode ? '#1F2937' : '#FFFFFF',
                        border: `1px solid ${isDarkMode ? '#374151' : '#E5E7EB'}`,
                        color: isDarkMode ? '#F9FAFB' : '#111827'
                    }}
                    labelFormatter={isDateField ? formatChartXAxis : undefined}
                />
                <Legend wrapperStyle={{ color: isDarkMode ? '#F9FAFB' : '#111827' }} />
                {areas.map((area, index) => (
                    <Area
                        key={area.dataKey}
                        type="monotone"
                        dataKey={area.dataKey}
                        name={area.name}
                        stroke={area.color || CHART_COLORS[index % CHART_COLORS.length]}
                        fill={area.color || CHART_COLORS[index % CHART_COLORS.length]}
                        fillOpacity={0.6}
                        stackId={stacked ? 'stack' : undefined}
                    />
                ))}
            </AreaChart>
        </ResponsiveContainer>
    );
};

/**
 * Sparkline Component - Mini line chart for KPI cards
 */
export const Sparkline = ({ data, dataKey, color = COLORS.primary, height = 60 }) => {
    return (
        <ResponsiveContainer width="100%" height={height}>
            <LineChart data={data}>
                <Line
                    type="monotone"
                    dataKey={dataKey}
                    stroke={color}
                    strokeWidth={2}
                    dot={false}
                />
            </LineChart>
        </ResponsiveContainer>
    );
};

/**
 * Stat Card Component - Enhanced KPI card with trend
 */
export const StatCard = ({ title, value, change, trend, icon, color = 'blue' }) => {
    const { isDarkMode } = useTheme();

    const colorClasses = {
        blue: {
            light: 'bg-blue-50 text-blue-600 border-blue-200',
            dark: 'bg-blue-900/30 text-blue-400 border-blue-700'
        },
        green: {
            light: 'bg-green-50 text-green-600 border-green-200',
            dark: 'bg-green-900/30 text-green-400 border-green-700'
        },
        red: {
            light: 'bg-red-50 text-red-600 border-red-200',
            dark: 'bg-red-900/30 text-red-400 border-red-700'
        },
        yellow: {
            light: 'bg-yellow-50 text-yellow-600 border-yellow-200',
            dark: 'bg-yellow-900/30 text-yellow-400 border-yellow-700'
        },
        purple: {
            light: 'bg-purple-50 text-purple-600 border-purple-200',
            dark: 'bg-purple-900/30 text-purple-400 border-purple-700'
        },
        indigo: {
            light: 'bg-indigo-50 text-indigo-600 border-indigo-200',
            dark: 'bg-indigo-900/30 text-indigo-400 border-indigo-700'
        }
    };

    const changeColor = change >= 0
        ? (isDarkMode ? 'text-green-400' : 'text-green-600')
        : (isDarkMode ? 'text-red-400' : 'text-red-600');
    const changeIcon = change >= 0 ? '↑' : '↓';

    return (
        <div className={`rounded-lg border p-6 ${isDarkMode ? colorClasses[color]?.dark : colorClasses[color]?.light || colorClasses.blue.light}`}>
            <div className="flex items-center justify-between mb-4">
                <div className="flex-1">
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>{title}</p>
                    <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{value}</p>
                    {change !== undefined && (
                        <p className={`text-sm font-medium mt-1 ${changeColor}`}>
                            {changeIcon} {Math.abs(change)}% from last period
                        </p>
                    )}
                </div>
                {icon && (
                    <div className="ml-4">
                        <div className="text-4xl opacity-50">{icon}</div>
                    </div>
                )}
            </div>
            {trend && trend.length > 0 && (
                <div className="mt-4">
                    <Sparkline data={trend} dataKey="value" color={color === 'green' ? COLORS.success : COLORS.primary} height={40} />
                </div>
            )}
        </div>
    );
};

/**
 * Metric Grid Component - Grid of stat cards
 */
export const MetricGrid = ({ metrics }) => {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {metrics.map((metric, index) => (
                <StatCard key={index} {...metric} />
            ))}
        </div>
    );
};

/**
 * Custom Tooltip Component
 */
export const CustomTooltip = ({ active, payload, label }) => {
    const { isDarkMode } = useTheme();

    if (active && payload && payload.length) {
        return (
            <div className={`p-4 rounded-lg shadow-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <p className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{label}</p>
                {payload.map((entry, index) => (
                    <div key={index} className="flex items-center justify-between gap-4">
                        <span className="flex items-center gap-2">
                            <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: entry.color }}
                            />
                            <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{entry.name}:</span>
                        </span>
                        <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{entry.value}</span>
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

/**
 * Chart Container Component - Wrapper with title and description
 */
export const ChartContainer = ({ title, description, children, actions }) => {
    const { isDarkMode } = useTheme();

    return (
        <div className={`${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow-md'} rounded-lg p-6 mb-6`}>
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
                    {description && (
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>{description}</p>
                    )}
                </div>
                {actions && (
                    <div className="flex gap-2">
                        {actions}
                    </div>
                )}
            </div>
            <div>{children}</div>
        </div>
    );
};

/**
 * Empty State Component
 */
export const EmptyState = ({ message = "No data available", icon }) => {
    const { isDarkMode } = useTheme();

    return (
        <div className={`flex flex-col items-center justify-center py-12 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {icon && <div className="text-6xl mb-4">{icon}</div>}
            <p className={`text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{message}</p>
        </div>
    );
};

/**
 * Loading State Component
 */
export const LoadingState = ({ message = "Loading data..." }) => {
    const { isDarkMode } = useTheme();

    return (
        <div className="flex flex-col items-center justify-center py-12">
            <div className={`animate-spin rounded-full h-12 w-12 border-b-2 mb-4 ${isDarkMode ? 'border-blue-400' : 'border-indigo-600'}`}></div>
            <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{message}</p>
        </div>
    );
};

export { COLORS, CHART_COLORS };
